import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  Album,
  ArrowLeft,
  Check,
  ChevronDown,
  Heart,
  Loader2,
  ListMusic,
  MoreHorizontal,
  Pause,
  Play,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useTheme } from '@/hooks/useTheme';
import { AudioPlayerProvider, useAudioPlayer, type MusicTrack } from '@/components/AudioPlayer';
import { parseAlbum, type MusicAlbum } from '@/components/AlbumManager';
import { LoginArea } from '@/components/auth/LoginArea';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTag(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([key]) => key === name)?.[1];
}

function parseTrack(event: NostrEvent): MusicTrack | null {
  const audio =
    event.tags
      .find(([key]) => key === 'imeta')
      ?.slice(1)
      .find((value) => value.startsWith('url '))
      ?.slice(4) ?? getTag(event, 'url');
  const title = getTag(event, 'title');
  if (!audio || !title) return null;
  return {
    id: event.id,
    title,
    artist: getTag(event, 'artist') ?? `${event.pubkey.slice(0, 8)}…`,
    album: getTag(event, 'album'),
    cover:
      getTag(event, 'image') ??
      'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=700&auto=format&fit=crop',
    audioUrl: audio,
    duration: Number(getTag(event, 'duration') ?? 0),
    genre: getTag(event, 'genre'),
    pubkey: event.pubkey,
    dTag: getTag(event, 'd'),
    tags: event.tags,
    createdAt: event.created_at,
    trackNumber: Number(getTag(event, 'track_number') ?? 0) || undefined,
  };
}

function formatDuration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '--:--';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function totalDuration(tracks: MusicTrack[]) {
  const secs = tracks.reduce((sum, t) => sum + (t.duration ?? 0), 0);
  if (secs <= 0) return null;
  const m = Math.floor(secs / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m} min`;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useAlbum(albumId: string) {
  const { nostr } = useNostr();
  return useQuery({
    queryKey: ['album', albumId],
    queryFn: async (context) => {
      const events = await nostr.query(
        [{ kinds: [36381], '#d': [albumId], limit: 1 }],
        { signal: context.signal },
      );
      const event = events[0];
      if (!event) return null;
      return parseAlbum(event);
    },
    staleTime: 30_000,
  });
}

function useAlbumTracks(album: MusicAlbum | null | undefined) {
  const { nostr } = useNostr();
  // Album trackRefs are "kind:pubkey:dTag" address references
  const dTags = useMemo(
    () =>
      (album?.trackRefs ?? []).flatMap((ref) => {
        const parts = ref.split(':');
        return parts.length >= 3 ? [parts.slice(2).join(':')] : [];
      }),
    [album?.trackRefs],
  );
  return useQuery({
    queryKey: ['album-tracks', album?.id ?? 'none', dTags.join(',')],
    enabled: Boolean(album && dTags.length > 0),
    queryFn: async (context) => {
      if (!album || dTags.length === 0) return [];
      const events = await nostr.query(
        [{ kinds: [36493], '#d': dTags, limit: 100 }],
        { signal: context.signal },
      );
      const parsed = events.map(parseTrack).filter((t): t is MusicTrack => t !== null);

      // Sort by track number if available, otherwise by album trackRefs order
      return parsed.sort((a, b) => {
        const aNum = a.trackNumber ?? Number.MAX_SAFE_INTEGER;
        const bNum = b.trackNumber ?? Number.MAX_SAFE_INTEGER;
        if (aNum !== bNum) return aNum - bNum;
        // Fall back to the order declared in trackRefs
        const aIdx = dTags.indexOf(a.dTag ?? '');
        const bIdx = dTags.indexOf(b.dTag ?? '');
        return aIdx - bIdx;
      });
    },
    staleTime: 30_000,
  });
}

function usePlaylists() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  return useQuery({
    queryKey: ['playlists', user?.pubkey],
    enabled: Boolean(user),
    queryFn: async (context) => {
      if (!user) return [];
      const events = await nostr.query(
        [{ kinds: [30004], authors: [user.pubkey], limit: 50 }],
        { signal: context.signal },
      );
      return events.map((e) => ({
        id: e.id,
        dTag: getTag(e, 'd') ?? e.id,
        title: getTag(e, 'title') ?? 'Untitled playlist',
        image: getTag(e, 'image'),
        trackRefs: e.tags.filter(([k]) => k === 'a').map(([, v]) => v),
        tags: e.tags,
        pubkey: e.pubkey,
      }));
    },
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Add-to-playlist dialog
// ---------------------------------------------------------------------------

interface PlaylistPickerDialogProps {
  track: MusicTrack;
  onClose: () => void;
}

function PlaylistPickerDialog({ track, onClose }: PlaylistPickerDialogProps) {
  const { user } = useCurrentUser();
  const { data: playlists = [], refetch } = usePlaylists();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const [open, setOpen] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const trackRef =
    track.dTag && track.pubkey
      ? `36493:${track.pubkey}:${track.dTag}`
      : null;

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const addToPlaylist = async (playlist: {
    dTag: string;
    title: string;
    trackRefs: string[];
    tags: string[][];
    pubkey: string;
  }) => {
    if (!user || !trackRef) return;
    const alreadyIn = playlist.trackRefs.includes(trackRef);
    if (alreadyIn) return;
    const allTags = [
      ...playlist.tags.filter(([k]) => !['a'].includes(k) || true),
      ['a', trackRef],
    ];
    // keep existing non-'a' tags, add the new track
    const baseTags = playlist.tags.filter(([k]) => k !== 'a');
    const existingARefs = playlist.tags.filter(([k]) => k === 'a').map(([, v]) => v);
    const newRefs = [...existingARefs, trackRef];
    await publish({
      kind: 30004,
      content: '',
      tags: [...baseTags, ...newRefs.map((ref) => ['a', ref])],
    });
    await refetch();
  };

  const createAndAdd = async () => {
    if (!user || !newName.trim() || !trackRef) return;
    setCreating(true);
    try {
      const dTag = `music/playlist/${newName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}`;
      await publish({
        kind: 30004,
        content: '',
        tags: [
          ['d', dTag],
          ['title', newName.trim()],
          ['alt', 'Music playlist'],
          ['a', trackRef],
        ],
      });
      await refetch();
      setNewName('');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="border-white/10 bg-[#1b1826] text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListMusic className="size-5 text-violet-300" />
            Add to playlist
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1 text-sm text-white/50 truncate">
          <span className="font-medium text-white/80">{track.title}</span>
          {track.artist ? ` · ${track.artist}` : ''}
        </div>

        {!user ? (
          <div className="rounded-xl border border-violet-300/20 bg-violet-300/8 p-5 text-sm text-white/65">
            Log in with your Nostr identity to manage playlists.
            <div className="mt-4">
              <LoginArea />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Existing playlists */}
            {playlists.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-white/4 p-2">
                {playlists.map((playlist) => {
                  const hasTrack = trackRef ? playlist.trackRefs.includes(trackRef) : false;
                  return (
                    <button
                      key={playlist.dTag}
                      onClick={() => addToPlaylist(playlist)}
                      disabled={isPending || hasTrack}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition',
                        hasTrack
                          ? 'cursor-default text-white/30'
                          : 'hover:bg-violet-500/15 hover:text-white',
                      )}
                    >
                      <ListMusic className="size-4 shrink-0 text-violet-300" />
                      <span className="flex-1 truncate">{playlist.title}</span>
                      {hasTrack && <Check className="size-4 text-violet-400" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Create new playlist */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-white/40">
                New playlist
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Playlist name…"
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
                  onKeyDown={(e) => { if (e.key === 'Enter') void createAndAdd(); }}
                />
                <Button
                  onClick={() => void createAndAdd()}
                  disabled={!newName.trim() || creating || isPending || !trackRef}
                  className="shrink-0 bg-violet-500 text-white hover:bg-violet-400"
                >
                  {creating ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
                </Button>
              </div>
              {!trackRef && (
                <p className="text-xs text-yellow-400/70">
                  This track doesn't have a Nostr address and cannot be saved to a playlist.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Track row
// ---------------------------------------------------------------------------

interface AlbumTrackRowProps {
  track: MusicTrack;
  index: number;
  favorite: boolean;
  onToggleFavorite: () => void;
  onAddToPlaylist: () => void;
}

function AlbumTrackRow({
  track,
  index,
  favorite,
  onToggleFavorite,
  onAddToPlaylist,
}: AlbumTrackRowProps) {
  const { currentTrack, isPlaying, playTrack, togglePlay } = useAudioPlayer();
  const isCurrent = currentTrack?.id === track.id;

  return (
    <div
      className={cn(
        'group flex items-center gap-3 rounded-xl px-3 py-2.5 transition',
        isCurrent
          ? 'bg-violet-500/15'
          : 'hover:bg-white/5',
      )}
    >
      {/* Track number / play indicator */}
      <div className="flex size-8 shrink-0 items-center justify-center text-sm text-white/40">
        <span className={cn('group-hover:hidden', isCurrent && 'hidden')}>
          {track.trackNumber ?? index + 1}
        </span>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => (isCurrent ? togglePlay() : playTrack(track))}
          className={cn(
            'hidden size-8 text-violet-300 hover:bg-transparent hover:text-violet-200',
            'group-hover:flex',
            isCurrent && 'flex',
          )}
          aria-label={isCurrent && isPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
        >
          {isCurrent && isPlaying ? (
            <Pause className="size-4 fill-current" />
          ) : (
            <Play className="size-4 fill-current" />
          )}
        </Button>
      </div>

      {/* Title + artist */}
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-sm font-medium', isCurrent ? 'text-violet-300' : 'text-white')}>
          {track.title}
        </p>
        <p className="truncate text-xs text-white/45">{track.artist}</p>
      </div>

      {/* Duration */}
      <span className="hidden text-xs text-white/35 sm:block">{formatDuration(track.duration)}</span>

      {/* Favorite */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleFavorite}
        className={cn(
          'size-8 text-white/35 opacity-0 transition group-hover:opacity-100 hover:bg-transparent',
          favorite && 'opacity-100 text-pink-400 hover:text-pink-300',
        )}
        aria-label={favorite ? `Remove ${track.title} from favorites` : `Add ${track.title} to favorites`}
      >
        <Heart className={cn('size-4', favorite && 'fill-current')} />
      </Button>

      {/* More options */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-white/35 opacity-0 transition group-hover:opacity-100 hover:bg-white/8 hover:text-white data-[state=open]:opacity-100"
            aria-label={`Options for ${track.title}`}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="border-white/10 bg-[#1e1b2a] text-white"
        >
          <DropdownMenuLabel className="text-xs text-white/40">Track options</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem
            onClick={() => (isCurrent ? togglePlay() : playTrack(track))}
            className="gap-2 focus:bg-violet-500/20 focus:text-white cursor-pointer"
          >
            {isCurrent && isPlaying ? (
              <><Pause className="size-4" /> Pause</>
            ) : (
              <><Play className="size-4" /> Play</>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onToggleFavorite}
            className="gap-2 focus:bg-violet-500/20 focus:text-white cursor-pointer"
          >
            <Heart className={cn('size-4', favorite && 'fill-current text-pink-400')} />
            {favorite ? 'Remove from favorites' : 'Add to favorites'}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onAddToPlaylist}
            className="gap-2 focus:bg-violet-500/20 focus:text-white cursor-pointer"
          >
            <ListMusic className="size-4" /> Add to playlist
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton states
// ---------------------------------------------------------------------------

function AlbumPageSkeleton() {
  return (
    <div className="min-h-screen bg-[#100e16] text-white">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#1e1830] to-[#100e16] px-5 py-10 sm:px-10 sm:py-14">
        <div className="mx-auto flex max-w-4xl flex-col items-start gap-8 sm:flex-row sm:items-end">
          <Skeleton className="aspect-square w-40 shrink-0 rounded-2xl sm:w-52" />
          <div className="space-y-3">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-9 w-56" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </div>
      {/* Tracks */}
      <div className="mx-auto max-w-4xl space-y-1 px-5 py-6 sm:px-10">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-2.5">
            <Skeleton className="size-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-2.5 w-24" />
            </div>
            <Skeleton className="h-3 w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main AlbumView (uses audio player context)
// ---------------------------------------------------------------------------

function AlbumView({ albumId }: { albumId: string }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const { data: album, isLoading: albumLoading } = useAlbum(albumId);
  const { data: tracks = [], isLoading: tracksLoading } = useAlbumTracks(album);
  const { playTrack, currentTrack, isPlaying, togglePlay } = useAudioPlayer();

  const [favoriteIds, setFavoriteIds] = useLocalStorage<string[]>('stritchtunes:favorites', []);
  const [playlistTarget, setPlaylistTarget] = useState<MusicTrack | null>(null);

  const isLoading = albumLoading || tracksLoading;

  const toggleFavorite = (id: string) =>
    setFavoriteIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );

  const playAll = () => {
    if (tracks.length === 0) return;
    playTrack(tracks[0]);
  };

  const isAlbumPlaying =
    currentTrack !== null &&
    tracks.some((t) => t.id === currentTrack.id) &&
    isPlaying;

  if (isLoading) return <AlbumPageSkeleton />;

  if (!album) {
    return (
      <div
        className={cn(
          'flex min-h-screen flex-col items-center justify-center gap-4 text-center',
          isDark ? 'bg-[#100e16] text-white' : 'bg-[#f7f5fb] text-[#211a32]',
        )}
      >
        <Album className="size-16 text-white/20" />
        <h1 className="text-2xl font-semibold">Album not found</h1>
        <p className="text-sm text-white/50">
          This album may have been removed or is not on a connected relay.
        </p>
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mt-2 gap-2 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft className="size-4" /> Back to home
        </Button>
      </div>
    );
  }

  const totalMin = totalDuration(tracks);
  const albumTypeLabel =
    album.albumType === 'ep' ? 'EP' : album.albumType === 'single' ? 'Single' : 'Album';

  return (
    <div
      className={cn(
        'min-h-screen pb-32',
        isDark ? 'bg-[#100e16] text-white' : 'sona-light bg-[#f7f5fb] text-[#211a32]',
      )}
    >
      {/* ── Hero banner ───────────────────────────────────────────────── */}
      <div className="relative overflow-hidden">
        {/* Blurred background image */}
        {album.cover && (
          <div
            className="absolute inset-0 scale-110 bg-cover bg-center opacity-25 blur-2xl"
            style={{ backgroundImage: `url(${album.cover})` }}
            aria-hidden
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#100e16]/60 to-[#100e16]" aria-hidden />

        <div className="relative mx-auto max-w-4xl px-5 py-10 sm:px-10 sm:py-14">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-8 flex items-center gap-2 text-sm text-white/50 transition hover:text-white"
          >
            <ArrowLeft className="size-4" /> Back
          </button>

          <div className="flex flex-col items-start gap-7 sm:flex-row sm:items-end">
            {/* Cover art */}
            <div className="aspect-square w-44 shrink-0 overflow-hidden rounded-2xl bg-white/8 shadow-2xl shadow-black/50 sm:w-52">
              {album.cover ? (
                <img
                  src={album.cover}
                  alt={`${album.title} cover`}
                  className="size-full object-cover"
                />
              ) : (
                <div className="flex size-full items-center justify-center text-violet-300">
                  <Album className="size-16" />
                </div>
              )}
            </div>

            {/* Meta */}
            <div className="flex-1 space-y-3">
              <Badge className="border-violet-300/20 bg-violet-300/10 text-violet-200 hover:bg-violet-300/10">
                {albumTypeLabel}
              </Badge>
              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                {album.title}
              </h1>
              <p className="text-base font-medium text-white/70">{album.artist}</p>

              <div className="flex flex-wrap items-center gap-2 text-sm text-white/40">
                {album.year && <span>{album.year}</span>}
                {album.year && <span>·</span>}
                <span>{tracks.length} track{tracks.length !== 1 ? 's' : ''}</span>
                {totalMin && <><span>·</span><span>{totalMin}</span></>}
              </div>

              {album.description && (
                <p className="max-w-lg text-sm leading-relaxed text-white/50">
                  {album.description}
                </p>
              )}

              {/* Action bar */}
              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={isAlbumPlaying ? togglePlay : playAll}
                  disabled={tracks.length === 0}
                  className="gap-2 bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-40"
                >
                  {isAlbumPlaying ? (
                    <><Pause className="size-4 fill-current" /> Pause</>
                  ) : (
                    <><Play className="size-4 fill-current" /> Play all</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Track list ────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-4xl px-5 sm:px-10">
        {tracks.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 py-14 text-center">
            <ListMusic className="mx-auto mb-4 size-10 text-white/20" />
            <p className="text-sm text-white/40">
              No tracks found for this album on connected relays.
            </p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {/* Column headers */}
            <div className="mb-2 flex items-center gap-3 border-b border-white/8 px-3 pb-2 text-[11px] uppercase tracking-wider text-white/30">
              <span className="w-8 text-center">#</span>
              <span className="flex-1">Title</span>
              <span className="hidden sm:block w-14 text-right pr-10">Time</span>
            </div>

            {tracks.map((track, index) => (
              <AlbumTrackRow
                key={track.id}
                track={track}
                index={index}
                favorite={favoriteIds.includes(track.id)}
                onToggleFavorite={() => toggleFavorite(track.id)}
                onAddToPlaylist={() => setPlaylistTarget(track)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Add-to-playlist dialog ─────────────────────────────────────── */}
      {playlistTarget && (
        <PlaylistPickerDialog
          track={playlistTarget}
          onClose={() => setPlaylistTarget(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page entry point (wraps in AudioPlayerProvider)
// ---------------------------------------------------------------------------

export default function AlbumPage() {
  const { albumId } = useParams<{ albumId: string }>();

  if (!albumId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#100e16] text-white">
        <p className="text-white/50">Invalid album identifier.</p>
      </div>
    );
  }

  return (
    <AudioPlayerProvider>
      <AlbumView albumId={albumId} />
    </AudioPlayerProvider>
  );
}
